import { z } from 'zod';

import { PASSWORD_MAX_UTF8_BYTES, utf8ByteLength } from './fieldRules';
import { personName } from './fieldSchemas';

/**
 * Identity rule #6 (docs/business/BusinessRules.md): min 8 characters, uppercase, lowercase, digit, and
 * special character. Shared between web and mobile registration/reset-password/change-password
 * forms — mirrors the backend's own validator for instant feedback; the backend remains
 * authoritative and re-validates on submit (docs/development/FrontendGuidelines.md §6.4).
 */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must include an uppercase letter')
  .regex(/[a-z]/, 'Password must include a lowercase letter')
  .regex(/\d/, 'Password must include a digit')
  .regex(/[^A-Za-z0-9]/, 'Password must include a special character')
  // BCrypt reads at most 72 BYTES; a longer password would silently equal any other sharing its first
  // 72 bytes. Bytes, not characters — "é" is two and an emoji four. Mirrors `NewPassword()` on the server.
  .refine(
    (value) => utf8ByteLength(value) <= PASSWORD_MAX_UTF8_BYTES,
    `Password is too long — use at most ${PASSWORD_MAX_UTF8_BYTES} bytes (fewer characters if it contains accents or emoji)`,
  );

/**
 * A ten-digit Indian mobile, beginning 6–9. Mirrors the backend's `IndianPhoneNumber.Pattern`
 * (`Willnq.Application/Modules/Identity/IndianPhoneNumber.cs`) — WillnQ operates in India, and the
 * `PhoneField` control renders a fixed `+91` adornment, so the country code is never part of the
 * stored value.
 *
 * Optional everywhere it appears: an empty string means "no number", which is a valid state and, on
 * the account form, a valid instruction to clear one.
 */
export const indianPhoneSchema = z
  .string()
  .regex(/^[6-9]\d{9}$/, 'Enter a 10-digit Indian mobile number')
  .optional()
  .or(z.literal(''));

export const loginSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  mfaCode: z.string().length(6, 'Enter the 6-digit code').optional(),
});

/**
 * Consent to the active Terms of Service and Privacy Policy (ADR-0026). An unchecked box is a validation
 * failure, not a valid `false` — producing that message is the whole point of the field. Modelled as a
 * refined boolean rather than `literal(true)` so the inferred form type stays `boolean` and the default
 * value can honestly be "not yet accepted" without a cast.
 *
 * The wording is identical to the backend's `LegalErrors.AcceptanceRequiredMessage`, which re-validates it
 * (`RegisterUserCommandValidator`) and refuses the account without it. This copy only spares the user a
 * round trip (docs/development/FrontendGuidelines.md §6.4).
 */
export const termsAcceptedSchema = z
  .boolean()
  .refine((accepted) => accepted, {
    message: 'You must accept the Terms of Service and Privacy Policy.',
  });

export const registerSchema = z
  .object({
    email: z.string().email('Enter a valid email address'),
    password: passwordSchema,
    confirmPassword: z.string(),
    firstName: personName('First name', 100),
    lastName: personName('Last name', 100),
    phone: indianPhoneSchema,
    termsAccepted: termsAcceptedSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

/** Exactly six digits — the server's rule; `length(6)` alone let a gap-padded "1 3456" through. */
export const mfaCodeSchema = z.string().regex(/^\d{6}$/, 'Enter the 6-digit code');

export const verifyEmailSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  otp: mfaCodeSchema,
});

export const forgotPasswordSchema = z.object({
  email: z.string().email('Enter a valid email address'),
});

/**
 * Pre-authentication MFA-reset stage 1 (lost authenticator, cannot sign in at all). The password
 * field re-enters an EXISTING password, so it takes the plain "is it present" check `loginSchema`
 * uses for the same reason — not the strength-checked `passwordSchema`, which is for choosing a NEW
 * one.
 */
export const forgotMfaSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const resetPasswordSchema = z
  .object({
    email: z.string().email('Enter a valid email address'),
    token: z.string().min(1, 'Reset token is required'),
    newPassword: passwordSchema,
    confirmNewPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
  });

/** The account's contact-details form (`PUT /account/phone`). Blank is valid — it clears the number. */
export const contactDetailsSchema = z.object({
  phone: indianPhoneSchema,
});

/**
 * `/account/change-password` for an account that already has a WillnQ password, including one that also
 * signs in with Google. `currentPassword` re-enters an existing secret, so it takes the plain
 * "is it present" check rather than the strength-checked `passwordSchema`, for the same reason
 * `loginSchema` does: the rules apply to the password being chosen, not to one already in use.
 */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
    confirmNewPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
  });

/**
 * The same screen for an account created through an external provider, which has no WillnQ password
 * (`CurrentUser.hasPassword === false`, BR-8). It is setting its first, so there is no current password
 * to confirm and the field is absent rather than present-and-disabled.
 *
 * <p>The new password goes through the identical `passwordSchema` and match check, so a first password
 * is held to the same complexity rules as a replacement. The server re-decides which schema applied from
 * the account regardless; this is convenience validation, never the authority
 * (FrontendGuidelines §6.4).</p>
 */
export const setPasswordSchema = z
  .object({
    newPassword: passwordSchema,
    confirmNewPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
  });
